/** @jsxImportSource @emotion/react */
import * as React from 'react';
import * as R from 'ramda';
import PassResortSelector, { ResortDropdownItem } from './PassResortSelector';
import { GuestDetails } from '../../../guestDetails';
import { ApiError } from '../../../../core/redux/api/apiError';
import { fullName } from '../../../module';
import { sortByDateProp, sortByProp, assoc } from '../../../../language/module';
import { Resort } from '../../../../resortDetails/resort';
import { apostrophize } from '../../../../language/strings';
import LabeledData from '../../../../forms/elements/LabeledData';
import ErrorView from '../../../../forms/ErrorView';
import { css } from '@emotion/react';
import { ColumnConfig } from '../../../../layout/table/columnConfig';
import { formatDate, toTwoDigitYearRanges } from '../../../../language/time';
import { HistoricalRedemption } from '../historicalRedemption';
import { RedemptionType } from '../redemptionType';
import { getRedemptionType } from '../module';
import Table from '../../../../layout/table/Table';
import ImmutableMap from '../../../../language/ImmutableMap';
import { ALL_RESORTS } from './module';
import { QuickEntitlement } from '../../models';
import { COLUMN_SPACING } from '../../../../forms/styles';
import { COOL_GREY } from '../../../../styles/colors';
import { getDescription, getPreferredPassInfo, getSeason } from '../../module';
import { FrappeData } from '../../../../charts/models';
import { EntitlementRedemptionHistory } from './EntitlementRedemptionHistory';
import { Loader } from '../../../../forms/elements/loader/Loader';
import { LargeText } from '../../../../styles/styles';
import  ReactFrappeChart  from 'react-frappe-charts';
import { EntitlementType } from '../../../../entitlementTypes/entitlementType';
import {combineSplitUsageDays} from '../../CombineSplitUsage';
import { getResource } from '../../../../language/resources/getResource';
import { BigSecondary } from '../../../../forms/elements/buttons';
import ModalView, { ModalContent } from '../../../../layout/modal/ModalView';
import ModalTitleHeader from '../../../../layout/modal/ModalTitleHeader';
import { PassUsageCorrectionModal } from './PassUsageCorrectionModal';
import { Redemption } from '../redemption';
import dtw from '../../../../assets/icons/daypass.svg';
import { MigrationRequest } from '../migrationRequest';
import { Dropdown } from '../../../../forms/elements/select/Dropdown';
import LabelGroup from '../../../../forms/elements/LabelGroup';
import { User } from '../../../../admin/userManagement/user';
import { AUTH0_USER_PREFIX } from '../../../../validationConfigurationTool/models';


const Divider = css`
  display: block;
  width: 100%;
  height: 2px;
  background-color: ${COOL_GREY};
`;

const ViewHistoricalLoaderContainer = css`
  display: flex; 
  flex-direction: column; 
  align-items: center; 
  padding: 25px 0;
`

const ViewHistoricalLoader = css`
  width: 100px;
  height: 100px;
  margin-bottom: 20px;
`

const LABEL_GROUP = css`
  flex-direction: row;
  align-items: baseline;
  margin-bottom: 0;
`;


export type Props = {
  guest:                 GuestDetails;
  passEntitlementId:     number;
  allEntitlementTypes:   ImmutableMap<EntitlementType>;
  allResorts:            ImmutableMap<Resort>;
  redemptions?:          EntitlementRedemptionHistory[];
  shouldShowChart?:      boolean;
  totalResortCount:      number;
  redemptionTypes?:      RedemptionType[];
  homeResort:            Resort;
  allowPassCorrection:   Boolean;

  // Loading
  isLoadingHistory?:     boolean,
  isRedeeming?:          boolean,

  // Error
  errorRedemptionHistory?:   ApiError,

  // Actions
  getRedemptionHistory?: (guestId: string) => void;
  bulkCorrection: (redemptions: Redemption[]) => void;
  migrateRedemptions: (request: MigrationRequest) => void;
  refreshDialog: () => void;

  resources: Record<string, any>;
  dateFormat: string;

  users: User[] | undefined;
  currentUserId: string;
};

export type State = {
  selectedPassId: number,
  selectedResort: string,
  selectedHistories: Set<HistoricalRedemption>,
  isPassUsageCorrectionModalOpen: boolean,
  correctionNotes: string,
  isMigration: boolean,
  targetPass: QuickEntitlement | undefined,
  sourcePass: QuickEntitlement | undefined
};


/**
 * ViewHistoricalTab: a component to display all redemptions of a selected entitlement for a guest from the beginning
 * of time.
 *
 * @guest                  - The guest that we're viewing the entitlements/redemptions of
 * @passEntitlementId      - The entitlement Id to use for retrieving the user's preferred pass if it is available
 * @allResorts             - A list of all resorts, needed to populate the names in the resort dropdown
 * @redemptions            - The list of entitlements and their redemptions used for the provided guest
 * @redemptionTypes        - A map of redemption types to use to display descriptions in the redemption history
 * @isLoadingHistory       - Whether or not we're awaiting the retrieval of the entitlement/redemption history
 * @isRedeeming            - Whether a correction is being redeemed and redemptions need to be refreshed
 * @errorRedemptionHistory - Whether there was an error retrieving the history
 * @getRedemptionHistory   - The action to dispatch and retrieve the guest's history
 * @resources              - The locale resource reference
 * @dateFormat             - The dateFormat to use for formatting dates
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class ViewHistoricalTab extends React.PureComponent<Props, State> {
  public state: State = {
    selectedPassId: -1,
    selectedResort: '',
    selectedHistories: new Set(),
    isPassUsageCorrectionModalOpen: false,
    correctionNotes: "",
    isMigration: false,
    targetPass: undefined,
    sourcePass: undefined
  };

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {

    const passMissingFromPrevState        = !prevState.selectedPassId || prevState.selectedPassId === -1;
    const redemptionsMissingFromPrevProps = !prevProps.redemptions || !prevProps.redemptions.length;
    const redemptionsFound                = this.props.redemptions && this.props.redemptions.length;

    if( (passMissingFromPrevState || redemptionsMissingFromPrevProps) && redemptionsFound) {
      this.setState(prev => R.mergeDeepRight(prev, {
        selectedPassId: this.mapPassesFor(this.props.redemptions!)[0].entitlementId,
        selectedResort: ALL_RESORTS,
      }));
    }

    if(this.props.isRedeeming !== prevProps.isRedeeming && !this.props.isRedeeming) {
      this.props.getRedemptionHistory!(this.props.guest.masterGuestId);
    }

  }

  public componentDidMount = () => {
    const startingPass =
      getPreferredPassInfo(this.props.passEntitlementId!, this.props.guest, this.props.allEntitlementTypes);
    const startingPassId = startingPass && startingPass.entitlementId;

    this.setState(prev => ({
      ...prev,
      selectedPassId: startingPassId || -1,
      selectedResort: ALL_RESORTS,
    }));

    this.props.getRedemptionHistory!(this.props.guest.masterGuestId);
  };


  private getSelectedEntitlement = (entitlementId: number, entitlements: EntitlementRedemptionHistory[]) => R.find(
    (entitlement) => entitlement.entitlementId === entitlementId, entitlements
  );


  private mapPassesFor = (entitlements: EntitlementRedemptionHistory[]): QuickEntitlement[] => R.sortBy(
    entitlement => entitlement.name,
    entitlements.map(pass => ({
      masterGuestId: pass.masterGuestId,
      entitlementId: pass.entitlementId,
      entitlementTypeId: pass.entitlementTypeId,
      entitlementCategoryId: pass.entitlementCatergoryId,
      // entitlementFulfillmentSeasonAttributes: [],
      entitlementStatusId: pass.entitlementStatusId,
      name: `${toTwoDigitYearRanges(pass.sellingSeasonDescription)} ${pass.entitlementDescription}`,
      expirationDate: '',
      effectiveDate: '',
      externalId: '',
      entitlementTypeAttributes: [],
      prepopulateGuest: false,
      isLocationRestricted: undefined,
    })))
  .reverse();


  private mapResortsForDropdown = (
    redemptionHistory: HistoricalRedemption[],
    allResorts: ImmutableMap<Resort>
  ): ResortDropdownItem[] => {
    if (!this.props.redemptions) {
      return [];
    }

    const uniqueResorts = R.uniqBy(
      (history => history.redeemResort),
      redemptionHistory,
    ).map(history => history.redeemResort);

    return [{ name: 'All Resorts', resortAbbrev: ALL_RESORTS }]
      .concat(allResorts
        .filter((key, resort) => uniqueResorts.includes(resort.resortName))
        .map((key, resort) => (
          {
            name:         resort.resortName,
            resortAbbrev: resort.resortName,
          })
        )
      );
  };


  private onPassChange = (entitlementId: number) => {
    this.setState(prev => R.mergeDeepRight(
      prev,
      { selectedPassId: entitlementId, selectedResort: ALL_RESORTS },
    ));

    this.setState({sourcePass: this.props.guest.entitlements?.find(x => x.entitlementId === entitlementId)})
  };

  private onTargetChange = (entitlement: QuickEntitlement) => {
    const thisEntitlement = this.props.guest.entitlements?.find(x => x.entitlementId === entitlement.entitlementId)
    this.setState({targetPass: thisEntitlement})
  }


  private onResortChange = (
    resortAbbrev: string,
  ) => this.setState(prev => assoc('selectedResort', resortAbbrev === 'ALL' ? ALL_RESORTS : resortAbbrev, prev));

  private dataFor = (redemptions: HistoricalRedemption[]): FrappeData => {
    const resorts: Array<{ resortName: string, history: HistoricalRedemption[] }> = [];

    // Map into an object array for sorting
    redemptions.forEach(redemption => {
      const resortExists = resorts.find(resort => resort.resortName === redemption.redeemResort);
      resortExists
      ? resortExists.history.push(redemption)
      : resorts.push({ resortName: redemption.redeemResort, history: [redemption] });
    });

    // Sort by most stays to least
    resorts.sort((a, b) => b.history.length - a.history.length);

    return {
      labels: resorts.map(resort => resort.resortName),
      datasets: [{values: resorts.map(resort => this.tallyStayDuration(resort.history))}]
    };
  };


  private toResorts = () => {
    const selected =
      this.props.redemptions && this.getSelectedEntitlement(this.state.selectedPassId, this.props.redemptions!);

    return selected
      ? this.mapResortsForDropdown(selected.redemptionHistory, this.props.allResorts!)
      : [];
  };

  private toNumberOfResortDays = (history: HistoricalRedemption[]) =>
    R.reduce((days, stay) => days + stay.redeemQuantity, 0, history);

  private tallyStayDuration = (history: HistoricalRedemption[]) => R.reduce(
    (sum, stay) => sum + stay.redeemQuantity, 0, history,
  );


  private renderRowWithTypes = (history: HistoricalRedemption) => this.renderRow(history, this.props.redemptionTypes);

  private onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, history: HistoricalRedemption) => {
    const selectedHistories = new Set(this.state.selectedHistories);

    if (e.target.checked) {
      selectedHistories.add(history);
    } else {
      selectedHistories.delete(history);
    }
    this.setState(prev => assoc('selectedHistories', selectedHistories, prev));
  }

  private redemptionFilter = redemption => this.state.selectedResort !== ALL_RESORTS
      ? redemption.redeemResort.includes(this.state.selectedResort)
      : true;

  private selected =
    this.props.redemptions && this.getSelectedEntitlement(this.state.selectedPassId, this.props.redemptions!);

  private history = combineSplitUsageDays(this.selected
    ? this.selected.redemptionHistory
    : []).filter(this.redemptionFilter);

  private entitlementHistory: HistoricalRedemption[] = [];

  private renderRow = (redemption: HistoricalRedemption, typeList: RedemptionType[] | undefined): React.ReactNode => {
    const hasReference = this.entitlementHistory.some(
        x => (x.redemptionIdReference as string === redemption.redemptionID as string 
           && x.redemptionIdReference as string !== x.redemptionID as string)
    );

    const canShowCheckbox =
      this.props.allowPassCorrection &&
      redemption.redeemQuantity > 0 &&
      redemption.redemptionSplitFactor === 1 &&
      !hasReference;

    var modifiedByName = ""
    if (redemption.modifiedById.startsWith(AUTH0_USER_PREFIX)) {
      // if no users - they may not be loaded yet
      if (!this.props.users || this.props.users.length === 0) {
        modifiedByName = getResource(this.props.resources, 'LIFT_ACCESS_HISTORY_USER_LOADING');
      } else {
        modifiedByName = this.props.users.find((user) => user.userId === redemption.modifiedById)?.userName
          || getResource(this.props.resources, 'LIFT_ACCESS_HISTORY_USER_NOT_FOUND');
      }
    } else {
      // User not an Auth0 user
      modifiedByName = redemption.modifiedById;
    }

    return (
      <tr key={redemption.redemptionID}>
        <td css={css`width: 150px;`}>{redemption.redemptionID}</td>
        <td>{ typeList ? getRedemptionType(typeList, redemption.redemptionTypeId) : redemption.redemptionTypeId }</td>
        <td css={css`width: 130px;`}>{formatDate(redemption.redeemDate, this.props.dateFormat)}</td>
        <td>{redemption.redeemResort} - {redemption.redeemLocation}</td>
        <td>{modifiedByName}</td>
        <td css={css`width: 60px; text-align: right;`}>{redemption.redeemQuantity}</td>
        <td css={css`max-width:600px; word-wrap: break-word; hyphens: auto;`}>
          {redemption.redemptionExternalCode}
        </td>

        <td>
          {canShowCheckbox && (
            <input
              type="checkbox"
              onChange={(e) => this.onCheckboxChange(e, redemption)}
            />
          )}
        </td>
      </tr>
    );
  };


  private setCorrectionNotes = (note: string) => {
    this.setState({correctionNotes: note})
  }

  private setIsDayCorrectionModalOpen = (value: boolean) => {
    this.setState({isPassUsageCorrectionModalOpen: value});
  }

  private setIsMigration = (value: boolean) => {
    this.setState({isMigration: value});
  }

  private doPassMigration = () => {
    if (!this.state.selectedHistories?.size || this.state.targetPass === undefined) return;

    const redemptionIds: number[] = Array.from(this.state.selectedHistories).map(history => Number(history.redemptionID));
    const request: MigrationRequest = {
      masterGuestId:        this.props.guest.masterGuestId,
      userId:               this.props.currentUserId,
      sourceEntitlementId:  this.state.selectedPassId,
      targetEntitlementId:  this.state.targetPass.entitlementId,
      redemptionIds:        redemptionIds
    }

    this.props.migrateRedemptions(request);
  }

  private doBulkCorrection = () => {
    if (!this.state.selectedHistories?.size) return;

    const redemptions: Redemption[] = Array.from(this.state.selectedHistories).map(history => ({
      guestId:               this.props.guest.masterGuestId,
      entitlementId:         this.state.selectedPassId || 0,
      redemptionDate:        new Date(history.redeemDate || ''),
      redemptionQuantity:    -1 * history.redeemQuantity,
      redemptionIdReference: `${history.redemptionID}`,
      redemptionExternalCode: this.state.correctionNotes,
      modifiedById:          `${history.modifiedById ?? ""}`,
    }));

    this.props.bulkCorrection(redemptions);
  };

  private onSave = () => {
    if (this.state.isMigration) {
      this.doPassMigration();
    }
    else {
      this.doBulkCorrection();
    }
    this.setIsDayCorrectionModalOpen(false);
    this.setCorrectionNotes("");
    window.setTimeout(()=>this.props.refreshDialog(), 500);
  }

  private getEntitlementId   = (item): string => R.prop('entitlementId', item) || '';
  private getEntitlementIdNumber = (item): number => R.prop('entitlementId', item)
  private getEntitlementName = (allEntitlementTypes) => (item): string =>
      `${getSeason(item, allEntitlementTypes)} ${getDescription(item, allEntitlementTypes)}`;

  public render = () => {

    const columnConfig: Array<ColumnConfig<HistoricalRedemption>> = [
      { id: 1, title: getResource(this.props.resources, 'VIEW_DAYS_TAB_COLUMN_REDEMPTION_ID'), onSort: sortByProp('redemptionID', true) },
      { id: 2, title: getResource(this.props.resources, 'VIEW_DAYS_TAB_COLUMN_TYPE'), onSort: sortByProp('redemptionTypeId', true) },
      {
        id: 3, 
        title:                   getResource(this.props.resources, 'VIEW_DAYS_TAB_COLUMN_DATE'),
        onSort:                  sortByDateProp('redeemDate'),
        defaultSortingColumn:    true,
        defaultSortingDirection: 'desc',
      },
      { id: 4, title: getResource(this.props.resources, 'VIEW_DAYS_TAB_COLUMN_RESORT'), onSort: sortByProp('redeemResort', true) },
      { id: 5, title: getResource(this.props.resources, 'VIEW_DAYS_TAB_COLUMN_MODIFIED_BY'), onSort: sortByProp('modifiedById', true) },
      { id: 6, title: getResource(this.props.resources, 'VIEW_DAYS_TAB_COLUMN_DAYS_USED'), onSort: sortByProp('redeemQuantity', false) },
      { id: 7, title: getResource(this.props.resources, 'VIEW_DAYS_TAB_COLUMN_NOTES'), onSort: sortByProp('redemptionExternalCode', true) },
    ];

    if (this.props.allowPassCorrection) {
      columnConfig.concat({ id: 9, title: ""})
    }

    if (this.props.isLoadingHistory) {
      return (
        <div css={COLUMN_SPACING}>
          {/* Guest Name */}
          <LabeledData label={getResource(this.props.resources, 'VIEW_DAYS_GUEST_LABEL')} data={fullName(this.props.guest)}/>

          <div css={ViewHistoricalLoaderContainer}>
            <Loader rootCss={ViewHistoricalLoader}/>
            <span css={css`display: block; ${LargeText};`}>{getResource(this.props.resources, 'VIEW_HISTORICAL_TAB_LOADING_DATA')}</span>
          </div>
        </div>
      );
    }

    const redemptionFilter = redemption => this.state.selectedResort !== ALL_RESORTS
      ? redemption.redeemResort.includes(this.state.selectedResort)
      : true;

    const selected =
      this.props.redemptions && this.getSelectedEntitlement(this.state.selectedPassId, this.props.redemptions!);

    const history = combineSplitUsageDays(selected
      ? selected.redemptionHistory
      : []).filter(redemptionFilter);

    this.entitlementHistory = history;

    const chartData = this.dataFor(history);

    const loadChart = () => {

      return chartData.labels.length > 0 && this.toNumberOfResortDays(history) > 0
              ? (
                <div>
                  <ReactFrappeChart type={'percentage'}
                                    data={chartData}
                  />
                </div>

    )
              : (<div css={css`display: flex; flex-direction: column; align-items: center;`}>
                  <span css={css`display: block; ${LargeText};`}>{getResource(this.props.resources, 'VIEW_DAYS_NO_DATA_FOUND_ERROR')}</span>
                </div>)
    };

    return (
      <div css={COLUMN_SPACING}>
        {/* Guest Name */}
        <LabeledData label={getResource(this.props.resources, 'VIEW_DAYS_RESORT_SELECTOR_PASS_LABEL')} data={fullName(this.props.guest)}/>

        <PassResortSelector passes={this.mapPassesFor(this.props.redemptions || [])}
                            allEntitlementTypes={this.props.allEntitlementTypes}
                            resorts={this.toResorts()}
                            selectedPass={this.state.selectedPassId}
                            selectedResort={this.state.selectedResort}
                            isLoadingResorts={!!this.props.isLoadingHistory}
                            onPassChange={this.onPassChange}
                            onResortChange={this.onResortChange}
                            resources={this.props.resources}/>

        <div css={Divider} />

        {/* Resorts For Entitlement Load Failure */}
        <ErrorView error={
          !this.props.isLoadingHistory && R.path([ 'errorPassResorts', 'errorMsg' ], this.props)
          ? getResource(this.props.resources, 'VIEW_DAYS_FAILED_FETCH', apostrophize(fullName(this.props.guest)))
          : undefined
        }/>

        {/* History for Resort Load Failure */}
        <ErrorView error={
          !this.props.isLoadingHistory
          && R.path([ 'errorResortHistory', 'errorMsg' ], this.props)
          ? getResource(this.props.resources, 'VIEW_DAYS_FAILED_HISTORY', apostrophize(fullName(this.props.guest)))
          : undefined
        }/>

        { this.state.selectedResort === ALL_RESORTS
          && <h2>{getResource(this.props.resources, 'VIEW_HISTORICAL_TAB_TOTAL_DAYS', this.toNumberOfResortDays(history))}</h2>
        }

        {
          this.props.shouldShowChart
          && loadChart()
        }

        {/* Skier Day Summary Table / Id, Type, Date, Quantity */}
        <Table columns={columnConfig}
              data={history}
              isLoading={this.props.isLoadingHistory!}
              noDataMessage={getResource(this.props.resources, 'VIEW_DAYS_NO_DATA')}
              renderRow={this.renderRowWithTypes}/>

        {this.state.isPassUsageCorrectionModalOpen && 
          <ModalView onDismiss={ () => (false)}>
            <ModalTitleHeader resources={this.props.resources} heading={getResource(this.props.resources, 'VIEW_DAYS_TAB_MODAL_HEADER')} icon={dtw} onDismiss={()=>this.setIsDayCorrectionModalOpen(false)}/>
            <ModalContent>
              <PassUsageCorrectionModal
                redemptions={this.state.selectedHistories}
                redemptionTypes={this.props.redemptionTypes}
                onSave={this.onSave}
                note={this.state.correctionNotes}
                onNoteChange={(note) => this.setCorrectionNotes(note)}
                targetEntitlement={this.state.targetPass}
                sourceEntitlement={this.state.sourcePass}
                isMigration={this.state.isMigration}
                allEntitlementTypes={this.props.allEntitlementTypes}
              />
            </ModalContent>
          </ModalView>
        }

        {this.state.isMigration && 
          <span>{getResource(this.props.resources, 'VIEW_HISTORICAL_TAB_MIGRATE_INFO')}</span>
        }

        {this.state.isMigration &&
            <LabelGroup label={getResource(this.props.resources, 'VIEW_DAYS_RESORT_SELECTOR_PASS_LABEL')} rootCss={LABEL_GROUP}>
        
              <Dropdown
                resources={this.props.resources}
                items={this.props.guest.entitlements!.filter(entitlement => entitlement.entitlementCategoryId === 1 
                                                          && entitlement.entitlementId !== this.state.selectedPassId)}
                getKey={this.getEntitlementId}
                getValue={this.getEntitlementId}
                getLabel={this.getEntitlementName(this.props.allEntitlementTypes)}
                noItemsMessage={getResource(this.props.resources, 'DTW_ENTITLEMENT_MODAL_NO_ITEMS')}
                onChange={this.onTargetChange}
                selectedItem={this.state.targetPass}
                rootCss={css`width: 350px;  max-width: 100%; margin-left: 10px;`} />
            </LabelGroup>
        }

        {this.state.selectedHistories.size > 0 && !this.state.isMigration &&
          <div css={css`display: flex; justify-content: flex-end;`}>
            <BigSecondary css={css`margin-right: 10px; margin-bottom: 30px;`} 
                          onClick={() => this.setIsDayCorrectionModalOpen(true)}>
              {getResource(this.props.resources, 'VIEW_HISTORICAL_TAB_REVERSE')}
            </BigSecondary>
            <BigSecondary css={css`margin-right: 10px; margin-bottom: 30px;`} 
                          onClick={() => this.setIsMigration(true)}>
              {getResource(this.props.resources, 'VIEW_HISTORICAL_TAB_MIGRATE')}
            </BigSecondary>
          </div>
        } 

        {this.state.isMigration &&
        <div css={css`display: flex; justify-content: flex-end;`}>
            <BigSecondary css={css`margin-right: 10px; margin-bottom: 30px;`} 
                          onClick={() => this.setIsMigration(false)}>
              {getResource(this.props.resources, 'VIEW_HISTORICAL_TAB_BACK')}
            </BigSecondary>
            <BigSecondary css={css`margin-right: 10px; margin-bottom: 30px;`} 
                          onClick={() => this.setIsDayCorrectionModalOpen(true)}>
              {getResource(this.props.resources, 'VIEW_HISTORICAL_TAB_NEXT')}
            </BigSecondary>
        </div>
        }
      </div>
    );
  };
};
